import PropTypes from 'prop-types';
import React, { useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'lodash-es';

import { CollapsibleContent, CollapsibleTitle, useCollapsibleState } from 'components/Collapsible';
import useChecklistDraftState from 'features/sort/useChecklistDraftState';
import isMappedTo from 'features/sort/isMappedTo';
import sortFilterGenericEvent from 'features/sort/sortFilterGenericEvent';
import setUtagDataForFdsDisplayType from 'features/sort/setUtagDataForFdsDisplayType';
import ChecklistDraftContext from 'features/sort/checklistDraftContext';
import {
	setDynamicAccordionState,
	setDynamicLastCollapsibleScrollTop,
	setDynamicLastAttributeViewportTop
} from 'features/sort/dynamicPersistenceStore';

import SortFilterAttrGroupHeader from '../SortFilterAttrGroupHeader';

import './SortFilterAttrGroupCollapsible.scss';

const SortFilterAttrGroupCollapsible = ({
	name,
	attributes,
	children,
	attributeGroupFilterDisplayTypeId,
	headerImageSrc,
	headerImageAltTag,
	isNavigating,
	setIsNavigating,
	isExpandedInitially = false
}) => {

	const dispatch = useDispatch();
	const isCollapsedDynamicState = useSelector((state) => state.ui.accordionCollapsed[name]);
	const lastScrollTopState = useSelector((state) => (state.ui.lastScrollTop.id === name ? state.ui.lastScrollTop.scrollTop : 0));
	const scrollRef = useRef(null);
	const containerRef = useRef(null);

	const firstAttribute = attributes[0];
	const attributeFilterDisplayTypeId = firstAttribute.AttributeFilterDisplayTypeId;

	const gridViewVariation = isMappedTo.attribute.attrValGridView.which(attributeFilterDisplayTypeId);
	const isMultiSelect = isMappedTo.attribute.multiSelect(attributeFilterDisplayTypeId);
	const gridImageSizeVariation = isMappedTo.attribute.attrValHeaderImageSize.which(attributeFilterDisplayTypeId);

	const defaultClassName = 'SortFilterAttrGroupCollapsible';
	const gridBaseClassName = 'SortFilterAttrGroupGridView';
	const cssSafeName = name.replace(/[^a-zA-Z0-9_-]/g, '');
	const fullClassName = classNames(defaultClassName, `${defaultClassName}--${cssSafeName}`, {
		[`${defaultClassName}--multiSelect`]: isMultiSelect,
		[gridBaseClassName]: gridViewVariation,
		[`${gridBaseClassName}--${gridViewVariation}`]: gridViewVariation,
		[`${gridBaseClassName}--${gridImageSizeVariation}`]: gridViewVariation && gridImageSizeVariation
	});
	const onOpen = () => {

		dispatch(setDynamicAccordionState({ id: name, isCollapsed: false }));

		// defer till after UI updates
		setTimeout(() => {

			setUtagDataForFdsDisplayType(name);
			sortFilterGenericEvent('filter_expand', name, '', true);

		}, 0);

	};
	const onClose = () => {

		dispatch(setDynamicAccordionState({ id: name, isCollapsed: true }));

		// defer till after UI updates
		setTimeout(() => {

			setUtagDataForFdsDisplayType(name);
			sortFilterGenericEvent('filter_collapse', name, '', true);

		}, 0);

	};

	const collapsible = useCollapsibleState(
		isCollapsedDynamicState !== undefined ? !isCollapsedDynamicState : isExpandedInitially
	);
	const applyCallback = () => {

		if (!containerRef.current) {

			return;

		}

		dispatch(setDynamicLastAttributeViewportTop({
			name,
			y: containerRef.current.getBoundingClientRect().top
		}));

	};
	const { applyMultiValueList } = useChecklistDraftState(firstAttribute, isNavigating, setIsNavigating, applyCallback);

	const handleScroll = useMemo(() => throttle(() => {

		if (scrollRef.current) {

			dispatch(setDynamicLastCollapsibleScrollTop({ id: name, scrollTop: scrollRef.current.scrollTop }));

		}

	}, 150), [ dispatch, name ]);

	const handleNativeScroll = (e) => {

		// we don't want to call FiltersContent scroll handler
		e.stopPropagation();
		handleScroll(e);

	};

	useEffect(() => {

		if (scrollRef.current) {

			scrollRef.current.scrollTop = lastScrollTopState;

		}

	}, []);

	return (
		<ChecklistDraftContext.Provider
			value={{
				applyMultiValueList,
				isNavigating,
				setIsNavigating
			}}
		>
			<div className={fullClassName} ref={containerRef}>
				<CollapsibleTitle disclosureIconVariant="plus" {...collapsible}>
					<SortFilterAttrGroupHeader
						name={name}
						attributeGroupFilterDisplayTypeId={attributeGroupFilterDisplayTypeId}
						headerImageSrc={headerImageSrc}
						headerImageAltTag={headerImageAltTag}
					/>
				</CollapsibleTitle>
				<CollapsibleContent
					onOpen={onOpen}
					onClose={onClose}
					onScroll={handleNativeScroll}
					ref={scrollRef}
					{...collapsible}
					deferAnimation={true}
				>
					{children}
				</CollapsibleContent>
			</div>
		</ChecklistDraftContext.Provider>
	);

};

SortFilterAttrGroupCollapsible.propTypes = {
	name: PropTypes.string.isRequired,
	children: PropTypes.oneOfType([ PropTypes.element ])
};

export default SortFilterAttrGroupCollapsible;
